import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { Label } from "../../../ui/label";
import { Loader2, Plus, Sparkles, ListTodo } from "lucide-react";
import { toast } from "sonner";
import Outliner from "../../../designView/outliner/Outliner";
import { OutlineItem } from "../../../designView/outliner/types";
import { UserTakeaway } from "../../../../api/userTakeaways";
import { breakdownAction, BreakdownSuggestion, createSubtasks } from "../../../../api/actionItemAI";
import useRetryFetch from "../../../../hooks/useRetryFetch";

interface BreakdownDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionItem: UserTakeaway | null;
  /** Called with how many sub-tasks were created. */
  onSubtasksCreated: (count?: number) => void;
}

const BreakdownDialog: React.FC<BreakdownDialogProps> = ({
  open,
  onOpenChange,
  actionItem,
  onSubtasksCreated,
}) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const [outlineItems, setOutlineItems] = useState<OutlineItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [inheritSettings, setInheritSettings] = useState(true);

  // Reset when dialog opens with new item
  useEffect(() => {
    if (open && actionItem) {
      setOutlineItems([]);
      setInheritSettings(true);
    }
  }, [open, actionItem?._id]);

  const generateId = () => `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handleAISuggest = async () => {
    if (!actionItem) return;

    setIsLoading(true);
    try {
      const response = await breakdownAction(retryFetch, {
        actionItemId: actionItem._id,
        content: actionItem.content,
        category: actionItem.category?.name,
        language: i18n.language,
      });

      if (response.status === "SUCCESS" && response.suggestions.length > 0) {
        const newItems: OutlineItem[] = response.suggestions.map((suggestion: BreakdownSuggestion) => ({
          id: generateId(),
          text: suggestion.content + (suggestion.estimatedTime ? ` (${suggestion.estimatedTime})` : ""),
          children: [],
          expanded: true,
        }));
        setOutlineItems(newItems);
        toast.success(t("action_items.breakdown.suggestions_loaded", "AI suggestions loaded"));
      } else {
        toast.error(t("action_items.breakdown.no_suggestions", "Could not generate suggestions"));
      }
    } catch (error) {
      console.error("Failed to get AI suggestions:", error);
      toast.error(t("action_items.breakdown.ai_error", "Failed to get AI suggestions"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddManually = () => {
    const newItem: OutlineItem = {
      id: generateId(),
      text: "",
      children: [],
      expanded: true,
    };
    setOutlineItems([...outlineItems, newItem]);

    // Focus the new item after render
    setTimeout(() => {
      const element = document.querySelector(`[data-item-id="${newItem.id}"]`) as HTMLElement;
      element?.focus();
    }, 50);
  };

  const handleCreateSubtasks = async () => {
    if (!actionItem || outlineItems.length === 0) return;

    // Filter out empty items
    const validItems = outlineItems.filter((item) => item.text.trim().length > 0);
    if (validItems.length === 0) {
      toast.error(t("action_items.breakdown.no_valid_items", "Please add at least one sub-task"));
      return;
    }

    setIsCreating(true);
    try {
      // Use the createSubtasks API to properly link sub-tasks to parent
      const response = await createSubtasks(retryFetch, {
        parentId: actionItem._id,
        subtasks: validItems.map((item) => ({
          content: item.text,
          priority: inheritSettings ? actionItem.priority : undefined,
          dueDate: inheritSettings ? actionItem.dueDate : undefined,
        })),
        inheritParentSettings: inheritSettings,
      });

      if (response.status === "SUCCESS") {
        toast.success(
          t("action_items.breakdown.subtasks_created", "{{count}} sub-tasks created", { count: response.created })
        );
        onSubtasksCreated(response.created);
        onOpenChange(false);
      } else {
        throw new Error(response.fault || "Failed to create sub-tasks");
      }
    } catch (error) {
      console.error("Failed to create sub-tasks:", error);
      toast.error(t("action_items.breakdown.create_error", "Failed to create sub-tasks"));
    } finally {
      setIsCreating(false);
    }
  };

  if (!actionItem) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-primary" />
            {t("action_items.breakdown.title", "Break Down")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col min-h-0 py-4">
          {/* Parent task display */}
          <div className="p-3 mb-4 rounded-lg bg-muted/50 border">
            <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">
              {t("action_items.breakdown.parent_task", "Parent Task")}
            </p>
            <p className="font-medium text-sm">{actionItem.content}</p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAISuggest}
              disabled={isLoading}
              className="gap-2"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {t("action_items.breakdown.ai_suggest", "AI Suggest")}
            </Button>
            <Button variant="outline" size="sm" onClick={handleAddManually} className="gap-2">
              <Plus className="h-4 w-4" />
              {t("action_items.breakdown.add_manually", "Add manually")}
            </Button>
          </div>

          {/* Outliner */}
          <div className="flex-1 min-h-0 overflow-auto border rounded-lg p-2 bg-background">
            {outlineItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-8 text-center text-muted-foreground">
                <ListTodo className="h-12 w-12 mb-3 opacity-30" />
                <p className="text-sm">
                  {t(
                    "action_items.breakdown.empty_state",
                    "Click 'AI Suggest' or 'Add manually' to start breaking down your task"
                  )}
                </p>
              </div>
            ) : (
              <Outliner
                data={outlineItems}
                onChange={setOutlineItems}
                readonly={false}
              />
            )}
          </div>

          {/* Options */}
          {outlineItems.length > 0 && (actionItem.priority || actionItem.category || actionItem.dueDate) && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t">
              <Checkbox
                id="inherit-settings"
                checked={inheritSettings}
                onCheckedChange={(checked) => setInheritSettings(checked === true)}
              />
              <Label htmlFor="inherit-settings" className="text-sm text-muted-foreground cursor-pointer">
                {t("action_items.breakdown.inherit_settings", "Copy priority, category, and due date from parent")}
              </Label>
              <p className="text-xs text-muted-foreground/60 ms-6">
                {t("action_items.breakdown.inherit_settings_hint", "If unchecked, sub-tasks will be created without these fields.")}
              </p>
            </div>
          )}

          {/* Info text */}
          {outlineItems.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              {t(
                "action_items.breakdown.subtasks_info_linked",
                "Sub-tasks will be linked to the parent. Complete all sub-tasks to complete the parent action."
              )}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
            {t("cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleCreateSubtasks}
            disabled={outlineItems.filter((i) => i.text.trim()).length === 0 || isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("creating", "Creating...")}
              </>
            ) : (
              t("action_items.breakdown.create_subtasks", "Create Sub-tasks")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BreakdownDialog;
